import { useState, type FormEvent } from 'react';
import { AlertCircle, Info, Loader2, Play, RefreshCw, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestEve, useTriggerEve } from './api';
import {
  SCENARIO_LABELS,
  SCENARIO_ORDER,
  eveForScenario,
  type EveAnalysisDetail,
  type EveInstrumentBreakdownDto,
  type IrrbbScenarioCode,
} from './types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Phase 2.3.EVE — Economic Value of Equity + Key Rate Duration page.
 *
 * Layout:
 *   1. Degraded banner (when placeholder rates were used)
 *   2. Calculate / Reload form
 *   3. Worst-case EVE highlight tile (regulatory capital focus)
 *   4. Six-scenario EVE table with a simple bar chart
 *   5. KRD bar chart — DV01 per tenor bucket
 */
export function EveAnalysisPage() {
  const tenantId = useTenantId();
  const [date, setDate] = useState<string>(todayISO());

  const latest  = useLatestEve(date);
  const trigger = useTriggerEve();

  const detail: EveAnalysisDetail | null = latest.data ?? null;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    trigger.mutate({ calculationDate: date, force: true });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          EVE — Economic Value of Equity
        </h1>
        <p className="text-sm text-muted-foreground">
          Basel III IRRBB sensitivity across six standard scenarios — tenant{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
        </p>
      </header>

      <DegradedBanner detail={detail} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run EVE</CardTitle>
          <CardDescription>
            Runs all six Basel IRRBB scenarios simultaneously — parallel ±200bp,
            short-rate ±250bp, steepener ±75bp, flattener ±75bp. No horizon cutoff.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="eve-date">Calculation date</Label>
              <Input
                id="eve-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-44"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => latest.refetch()}
              disabled={latest.isFetching}
            >
              {latest.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Reload
            </Button>
            <Button type="submit" disabled={trigger.isPending}>
              {trigger.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run on-demand
            </Button>
          </form>

          {trigger.isError  ? <ApiErrorBanner error={trigger.error} /> : null}
          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation complete · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {latest.isError ? <ApiErrorBanner error={latest.error} /> : null}

      {latest.isLoading && !detail ? (
        <AnalysisSkeleton />
      ) : (
        <>
          {/* Worst-case highlight */}
          <WorstCaseTile detail={detail} loading={latest.isLoading} />

          {/* Six-scenario table */}
          <ScenarioTable detail={detail} loading={latest.isLoading} />

          {/* KRD chart */}
          <KrdSection detail={detail} loading={latest.isLoading} />

          {/* Phase 4.2.EVE — instrument breakdown */}
          {detail?.instrumentBreakdown && detail.instrumentBreakdown.length > 0 ? (
            <InstrumentBreakdownCard rows={detail.instrumentBreakdown} />
          ) : null}
        </>
      )}
    </div>
  );
}

// ── Degraded banner ──────────────────────────────────────────────────────────

function DegradedBanner({ detail }: { detail: EveAnalysisDetail | null }) {
  if (!detail || !detail.degradedMode) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <strong>Degraded mode — placeholder rates used.</strong> No validated
        yield-curve snapshot was available for {detail.calculationDate}. Upload
        + validate a curve on the <em>Parameters → Yield Curves</em> page to get
        curve-aware EVE.
      </p>
    </div>
  );
}

// ── Worst-case tile ───────────────────────────────────────────────────────────

function WorstCaseTile({
  detail,
  loading,
}: {
  detail: EveAnalysisDetail | null;
  loading: boolean;
}) {
  const isLoss = detail && Number(detail.worstCaseEve) < 0;
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card className={cn(isLoss && 'border-destructive/40 bg-destructive/5')}>
        <CardHeader className="pb-2">
          <CardDescription className="flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" />
            Worst-case EVE
          </CardDescription>
          <CardTitle
            className={cn('text-2xl', isLoss && 'text-destructive')}
          >
            {detail ? formatCcy(detail.worstCaseEve) : loading ? '…' : '—'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-xs text-muted-foreground">
          {detail?.worstCaseScenario
            ? SCENARIO_LABELS[detail.worstCaseScenario as IrrbbScenarioCode]
            : '—'}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Worst-case EVE / PV</CardDescription>
          <CardTitle className={cn('text-2xl', isLoss && 'text-destructive')}>
            {detail ? formatPct(detail.worstCaseEvePct) : loading ? '…' : '—'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-xs text-muted-foreground">
          Equity sensitivity ratio
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Portfolio PV (baseline)</CardDescription>
          <CardTitle className="text-2xl">
            {detail ? formatCcy(detail.portfolioPv) : loading ? '…' : '—'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-xs text-muted-foreground">
          {detail ? `Engine ${detail.engineVersion}` : '—'}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Six-scenario table ────────────────────────────────────────────────────────

function ScenarioTable({
  detail,
  loading,
}: {
  detail: EveAnalysisDetail | null;
  loading: boolean;
}) {
  if (!detail && loading) {
    return <p className="text-sm text-muted-foreground">Loading scenarios…</p>;
  }

  // Find max absolute EVE for bar scaling
  const absValues = SCENARIO_ORDER.map((s) =>
    detail ? Math.abs(Number(eveForScenario(detail, s))) : 0,
  );
  const maxAbs = Math.max(...absValues, 1);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">EVE by Basel Scenario</CardTitle>
        <CardDescription>
          PV_baseline − PV_stressed. Negative = equity loss under that shock.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {SCENARIO_ORDER.map((scenario) => {
            const raw   = detail ? eveForScenario(detail, scenario) : null;
            const value = raw != null ? Number(raw) : null;
            const isWorst = detail?.worstCaseScenario === scenario;
            const barPct  = value != null ? (Math.abs(value) / maxAbs) * 100 : 0;
            const isNeg   = value != null && value < 0;

            return (
              <div key={scenario} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span
                    className={cn(
                      'font-medium',
                      isWorst && 'text-destructive',
                    )}
                  >
                    {SCENARIO_LABELS[scenario]}
                    {isWorst ? (
                      <span className="ml-2 rounded bg-destructive/15 px-1 py-0.5 text-[10px] font-semibold uppercase text-destructive">
                        worst
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      'tabular-nums',
                      isNeg ? 'text-destructive' : 'text-emerald-700',
                    )}
                  >
                    {value != null ? formatCcy(String(value)) : '—'}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      isNeg ? 'bg-destructive/70' : 'bg-emerald-500/70',
                    )}
                    style={{ width: `${barPct.toFixed(1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── KRD section ───────────────────────────────────────────────────────────────

function KrdSection({
  detail,
  loading,
}: {
  detail: EveAnalysisDetail | null;
  loading: boolean;
}) {
  if (!detail && !loading) return null;
  const buckets = detail?.krdBuckets ?? [];
  if (buckets.length === 0) return null;

  const maxDv01 = Math.max(...buckets.map((b) => Math.abs(Number(b.dv01))), 1);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Key Rate Duration — DV01 per Tenor Bucket</CardTitle>
        <CardDescription>
          DV01 [CCY per 1bp] attributable to cashflows in each standard tenor band.
          Sum of all buckets = portfolio DV01.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && !detail ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="flex items-end gap-2" style={{ height: '120px' }}>
            {buckets.map((b) => {
              const abs  = Math.abs(Number(b.dv01));
              const pct  = (abs / maxDv01) * 100;
              const isNeg = Number(b.dv01) < 0;
              return (
                <div
                  key={b.tenorLabel}
                  className="group flex flex-1 flex-col items-center justify-end gap-1"
                >
                  <div
                    className={cn(
                      'w-full rounded-t transition-all',
                      isNeg ? 'bg-destructive/60' : 'bg-primary/60',
                    )}
                    style={{ height: `${Math.max(pct, 2).toFixed(1)}%` }}
                    title={`${b.tenorLabel}: ${b.dv01}`}
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {b.tenorLabel}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Formatters ────────────────────────────────────────────────────────────────

function formatCcy(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs  = Math.abs(n);
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${(abs / 1_000).toFixed(2)}K`;
  return `${sign}${abs.toFixed(2)}`;
}

function formatPct(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  return (n * 100).toFixed(2) + '%';
}

/**
 * Abbreviates a large decimal-string amount (B / M / K).
 * Mirrors the same helper in NimAnalysisPage.
 */
function formatAmount(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B';
  if (abs >= 1_000_000)     return (n / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)         return (n / 1_000).toFixed(2) + 'K';
  return n.toFixed(2);
}

// ── InstrumentBreakdownCard (Phase 4.2.EVE) ───────────────────────────────────

/**
 * Per-instrument-type EVE sensitivity table.
 *
 * Shows each instrument book's baseline PV and EVE across all six Basel
 * scenarios so the ALM committee can see which books drive interest-rate
 * risk. Fixed-rate books (BOND, LOAN with long duration) should show higher
 * |EVE| than floating-rate books (INTERBANK, DAV, OVERDRAFT) which reprice
 * quickly under the full re-pricing model.
 *
 * Columns: Instrument | Portfolio PV | ↑200 | ↓200 | Short↑ | Short↓ | Steep | Flat | Worst EVE
 */
function InstrumentBreakdownCard({ rows }: { rows: EveInstrumentBreakdownDto[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">EVE by Instrument Type</CardTitle>
        <CardDescription>
          Per-book EVE sensitivity under all six Basel IRRBB scenarios. Full
          re-pricing model: floating-rate instruments (INTERBANK, DAV, OVERDRAFT)
          show lower sensitivity as they reprice to market at their reset date.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="pb-2 pr-4 font-medium">Instrument</th>
                <th className="pb-2 pr-4 text-right font-medium">Portfolio PV</th>
                <th className="pb-2 pr-3 text-right font-medium">↑200bp</th>
                <th className="pb-2 pr-3 text-right font-medium">↓200bp</th>
                <th className="pb-2 pr-3 text-right font-medium">Short↑</th>
                <th className="pb-2 pr-3 text-right font-medium">Short↓</th>
                <th className="pb-2 pr-3 text-right font-medium">Steep</th>
                <th className="pb-2 pr-3 text-right font-medium">Flat</th>
                <th className="pb-2 text-right font-medium">Worst EVE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const worst = Number(row.worstCaseEve);
                const isNegWorst = worst < 0;
                return (
                  <tr key={row.instrumentType} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-4">
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
                        {row.instrumentType}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-xs tabular-nums">
                      {formatAmount(row.portfolioPv)}
                    </td>
                    {(
                      ['PARALLEL_UP_200', 'PARALLEL_DOWN_200',
                       'SHORT_RATE_UP_250', 'SHORT_RATE_DOWN_250',
                       'STEEPENER_150', 'FLATTENER_150'] as IrrbbScenarioCode[]
                    ).map((s) => {
                      const v = Number(row.eveByScenario[s] ?? '0');
                      return (
                        <td
                          key={s}
                          className={cn(
                            'py-2 pr-3 text-right font-mono text-xs tabular-nums',
                            v < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400',
                          )}
                        >
                          {formatAmount(row.eveByScenario[s] ?? '0')}
                        </td>
                      );
                    })}
                    <td
                      className={cn(
                        'py-2 text-right font-mono text-xs font-semibold tabular-nums',
                        isNegWorst ? 'text-destructive' : 'text-green-600 dark:text-green-400',
                      )}
                    >
                      {formatAmount(row.worstCaseEve)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
